// HTTP client — typed wrappers around fetch targeting the Hono server.
// Three modes resolved at runtime:
//   • Electron (file://) — absolute URL to the local server.
//   • Same-origin desktop — empty base, relative paths.
//   • Relay path (https://relay/d/<deviceId>/) — base includes the device prefix.
import { desktopOnlyRoute } from '@onestone/shared/surfaces';
import { isCloud } from './surface';

function resolveBaseUrl(): string {
  if (typeof window === 'undefined' || !window.location.protocol.startsWith('http')) {
    // Prefer runtime port from Electron preload (always matches the actual
    // server), fall back to Vite build-time env, then default. The preload
    // bridge (__oneStoneConfig.serverPort) is set synchronously before any
    // renderer code — it resolves the build-time vs runtime port mismatch
    // that breaks custom-port, test, and standalone-app scenarios.
    const port =
      (typeof window !== 'undefined' ? window.__oneStoneConfig?.serverPort : undefined) ??
      import.meta.env.VITE_SERVER_PORT ??
      '3850';
    return `http://127.0.0.1:${port}`;
  }
  // Detect /d/<deviceId>/ relay prefix in the current pathname. The relay
  // strips this prefix before forwarding, so the server stays oblivious — the
  // renderer just needs to send API calls back to the same prefix.
  const match = window.location.pathname.match(/^\/d\/([a-f0-9]{16,64})(?:\/|$)/i);
  if (match) {
    return `${window.location.origin}/d/${match[1]}`;
  }
  return '';
}
export const BASE_URL = resolveBaseUrl();

export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

/**
 * On the cloud, a route only the desktop serves (`DESKTOP_ONLY_ROUTES`) is
 * answered here with its reason — 501, never a request that would 404 — so a
 * page that reaches one says why instead of failing vaguely.
 */
function offSurface(path: string): { reason: string } | undefined {
  if (!isCloud() || path.startsWith('http')) return undefined;
  return desktopOnlyRoute(path);
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  json?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

const TOKEN_STORAGE_KEY = 'onestone-api-token';

/** Read the API token stashed by mobile / remote clients. */
export function getApiToken(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

/** Persist (or clear when empty) the API token used for non-loopback access. */
export function setApiToken(token: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (token) window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* localStorage may be disabled — best-effort */
  }
}

const unauthorizedListeners = new Set<() => void>();

/**
 * Hear every 401 from OneStone's own API — the sign that this page's token or
 * session is no longer accepted (revoked while the page was open, expired).
 * The host's gate listens and re-checks; returns the unsubscribe.
 */
export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

/** Announce a 401 from our own API (an absolute URL is someone else's server). */
function noteStatus(path: string, status: number): void {
  if (status !== 401 || path.startsWith('http')) return;
  for (const listener of [...unauthorizedListeners]) listener();
}

/**
 * Raw fetch that resolves `path` against BASE_URL and attaches the saved
 * Bearer token. Use for endpoints that need streaming bodies (chat SSE,
 * uploads) instead of the JSON `api()` helper.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const missing = offSurface(path);
  if (missing) {
    return new Response(JSON.stringify({ error: missing.reason }), {
      status: 501,
      headers: { 'Content-Type': 'application/json' },
    });
  }
  const headers = new Headers(init.headers);
  const token = getApiToken();
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;
  const res = await fetch(url, { ...init, headers });
  noteStatus(path, res.status);
  return res;
}

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const missing = offSurface(path);
  if (missing) throw new ApiError(missing.reason, 501);
  const headers: Record<string, string> = { ...options.headers };
  if (options.json !== undefined) headers['Content-Type'] = 'application/json';
  // Attach the saved API token on every request — the desktop dashboard
  // typically never has one set, so this is a no-op for loopback users.
  const token = getApiToken();
  if (token && !headers['Authorization']) headers['Authorization'] = `Bearer ${token}`;

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
  };
  if (options.json !== undefined) init.body = JSON.stringify(options.json);
  if (options.signal) init.signal = options.signal;

  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;
  const res = await fetch(url, init);
  noteStatus(path, res.status);
  const text = await res.text();
  let body: unknown = null;
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body && typeof body.error === 'string'
        ? body.error
        : res.statusText || `Request failed with ${res.status}`;
    throw new ApiError(message, res.status);
  }

  return body as T;
}

/** Extract a human-readable message from a mutation error (ApiError, Error, or unknown). */
export function mutationError(err: unknown, fallback = 'Request failed'): string {
  if (err instanceof ApiError || err instanceof Error) return err.message;
  return fallback;
}

/** Typed convenience wrappers. */
export const get = <T>(path: string, options?: Omit<ApiOptions, 'method'>): Promise<T> =>
  api<T>(path, { ...options, method: 'GET' });

export const post = <T>(path: string, json?: unknown, options?: Omit<ApiOptions, 'method' | 'json'>): Promise<T> =>
  api<T>(path, { ...options, method: 'POST', json });

export const put = <T>(path: string, json?: unknown, options?: Omit<ApiOptions, 'method' | 'json'>): Promise<T> =>
  api<T>(path, { ...options, method: 'PUT', json });
